// Initial state is the place you define all initial values for the Redux store of the feature.
// In the 'standard' way, initialState is defined in reducers: http://redux.js.org/docs/basics/Reducers.html
// But when application grows, there will be multiple reducers files, it's not intuitive what data is managed by the whole store.
// So Rekit extracts the initial state definition into a separate module so that you can have
// a quick view about what data is used for the feature, at any time.

// NOTE: initialState constant is necessary so that Rekit could auto add initial state when creating async actions.
const initialState = {
  columns: [],
  containers: [],
  folders: {},
  documents: [],
  matches: [],
  totalDocuments: 0,
  uploadFiles: [],
  tags: [],
  tagGroups: [],
  authorsList: null,
  recipientsList: null,
  annotations: [],
  pageRefs: [],
  answer: null,
  answers: null,
  searchHistory: [],
  fileSearchHistory: [],
  groups: [],
  aiToolsResponse: [],
  realTimeTranscriptLines: [],
  realTimeTranscriptLastEventAt: null,
  realTimeTranscriptSessionKey: null,
  realTimeTranscriptSessionRestartedAt: null,
  realTimeReindexRangePending: null,
  realTimeNoteNotifications: [],
  aiToolsFilters: {},
  askAiSearchType: null,
  askAiSseStatus: 'idle',
  askAiSsePrompt: '',
  askAiSseAssistantText: '',
  askAiSseProgress: [],
  askAiSseError: null,
  askAiSseChatId: null,
  askAiSseHasFirstChunk: false,
  askAiSseHistory: [],
  askAiSseCurrentId: null,
  askAiSseSelectedBundles: [],
  askAiSseSelectedFiles: [],
  askAiSseSelectedFilesType: null,
  askAiSseResetCount: 0,
  fetchAskAiSseChatMessagesPending: false,
  fetchAskAiSseChatMessagesError: null,
  askAiSseChatMessagesResponse: null,
  searchMatchesInput: [],
  directAnswersFilters: null,
  searchAnsChat: [],
  searchSuggestedQuestions: null,
  clickedSource: null,
  timeline: null,
  timelines: null,
  typedResponseCount: 0,
  currentSelectedFile: null,
  lastSelectedFile: null,
  lastSelectedCategory: null,
  lastSelectedBundle: null,
  fileToCompare: null,
  filePipelineStatus: {},
  fetchFilePipelineStatusPending: {},
  filePipelineStatusError: {},
  caseRegionTemp: null,
  featuresAndPermissions: {},
  globalPageLookup: null,
  entities: null,
  sortDirection: null,
  sortByColumn: null,
  fetchFoldersPending: true,
  fetchFoldersFeedback: null,
  fetchContainersPending: false,
  fetchContainersFeedback: null,
  fetchFolderPending: false,
  fetchFolderFeedback: null,
  fetchFolderAfterSortPending: false,
  fetchDocumentsPending: false,
  fetchMatchDocumentsPending: false,
  fetchDocumentsFeedback: null,
  isDeepSearchPerformed: false,
  uploadFilesPending: false,
  uploadFilesFeedback: null,
  uploadFilesTooLargeFeedback: null,
  uploadFilesProgressClosed: false,
  assignAnnotationsPending: false,
  assignAnnotationsFeedback: null,
  assignDocumentsPending: false,
  assignDocumentsFeedback: null,
  unassignDocumentsPending: false,
  unassignDocumentsFeedback: null,
  unassignDocumentMultiplePending: false,
  unassignDocumentMultipleFeedback: null,
  addFolderPending: false,
  addFolderFeedback: null,
  deleteFolderPending: false,
  deleteFolderFeedback: null,
  updateFolderPending: false,
  updateFolderFeedback: null,
  fetchDocumentPending: false,
  fetchDocumentFeedback: null,
  fetchDocumentPartial: false,
  updateDocumentPending: false,
  updateDocumentFeedback: null,
  updateDocumentSortPending: false,
  updateDocumentSortFeedback: null,
  mergeDocumentsPending: false,
  mergeDocumentsFeedback: null,
  templates: null,
  fetchTemplatesPending: false,
  fetchTemplatesFeedback: null,
  fetchTeamsPending: false,
  fetchTeamsFeedback: null,
  getTeamActivityLogsPending: false,
  getTeamActivityLogsFeedback: null,
  getMoreInfoDocumentPending: false,
  getMoreInfoDocumentError: null,
  getMoreInfoFolderPending: false,
  getFolderStatsPending: false,
  getMoreInfoFolderError: null,
  getFolderStats: null,
  dismissGetFolderStatsFeedback: null,
  fetchLayoutsPending: false,
  fetchLayoutsError: null,
  updateLayoutsPending: false,
  updateLayoutsError: null,
  fetchCaseDetailsPending: true,
  fetchCaseDetailsError: null,
  updateCasePending: false,
  updateCaseError: null,
  updateDocumentMetaPending: false,
  updateDocumentMetaError: null,
  fetchTsAndCsPending: false,
  fetchTsAndCsFeedback: null,
  acceptTsAndCsPending: false,
  acceptTsAndCsFeedback: null,
  fetchFileDetailsPending: false,
  fetchFileDetailsFeedback: null,
  replaceFilePending: false,
  replaceFileFeedback: null,
  duplicateFilePending: false,
  duplicateFileFeedback: null,
  generateHyperlinksPending: false,
  generateHyperlinksFeedback: null,
  updateReferencesPending: false,
  updateReferencesFeedback: null,
  updateReferencesResult: null,
  updateReferencesCompletedByFileId: {},
  generateReferenceCandidatesPending: false,
  generateReferenceCandidatesFeedback: null,
  generateReferenceCandidatesResult: null,
  generateReferenceSequencePending: false,
  generateReferenceSequenceFeedback: null,
  generateReferenceSequenceResult: null,
  takeReferencePending: false,
  takeReferenceFeedback: null,
  takeReferenceResult: null,
  takeReferenceWithNamesPending: false,
  takeReferenceWithNamesFeedback: null,
  takeReferenceWithNamesResult: null,
  fetchExhibitChangesPending: false,
  fetchExhibitChangesFeedback: null,
  exhibitChangesData: null,
  fetchUserDetailsPending: false,
  fetchUserDetailsFeedback: null,
  fetchTeamPending: false,
  fetchTeamFeedback: null,
  updateTeamMemberPending: false,
  updateTeamMemberFeedback: null,
  removeTeamMemberPending: false,
  removeTeamMemberFeedback: null,
  sendInvitePending: false,
  sendInviteFeedback: null,
  fetchInviteLinkPending: false,
  fetchInviteLinkFeedback: null,
  acceptTeamInvitePending: false,
  acceptTeamInviteFeedback: null,
  fetchAdminsPending: false,
  fetchAdminsFeedback: null,
  fetchModeratorsPending: false,
  fetchModeratorsFeedback: null,
  addCaseRolesUserPending: false,
  addCaseRolesUserFeedback: null,
  removeCaseRolesUserPending: false,
  removeCaseRolesUserFeedback: null,
  addSystemTestPending: false,
  addSystemTestFeedback: null,
  fetchSystemTestPending: false,
  fetchSystemTestFeedback: null,
  fetchTagsPending: false,
  fetchTagsFeedback: null,
  createTagPending: false,
  createTagFeedback: null,
  deleteTagPending: false,
  deleteTagFeedback: null,
  updateTagPending: false,
  updateTagFeedback: null,
  fetchFileVersionsPending: false,
  fetchFileVersionsFeedback: null,
  updateFileVersionsPending: false,
  updateFileVersionsFeedback: null,
  createFileVersionPending: false,
  createFileVersionFeedback: null,
  fetchDialInNumbersPending: false,
  fetchDialInNumbersFeedback: null,
  updateDialInNumbersPending: false,
  updateDialInNumbersFeedback: null,
  uploadConfidentialityRingFilesPending: false,
  uploadConfidentialityRingFilesFeedback: null,
  generateAnswersPending: false,
  generateAnswersFeedback: null,
  showDocFinderFilter: true,
  showDocFinderBookmarks: false,
  displayDataMapFailedRows: false,
  viewerControl: null,
  caseArtefacts: null,
  caseArtefactsPending: false,
  caseArtefactsFeedback: null,
  crawlSummary: null,
  crawlSummaryPending: false,
  crawlSummaryFeedback: null,
  crawlSummaryHint: null,
  caseTagSuggestions: null,
  caseTagSuggestionsPending: false,
  caseTagSuggestionsFeedback: null,
  acceptCaseTagSuggestionPending: false,
  acceptCaseTagSuggestionFeedback: null,
  acceptCaseTagSuggestionTarget: null,
  rejectCaseTagSuggestionPending: false,
  rejectCaseTagSuggestionFeedback: null,
  rejectCaseTagSuggestionTarget: null,
  triggerCrawlPending: false,
  triggerCrawlFeedback: null,
  selectedArtefact: null,
  selectedArtefactPending: false,
  selectedArtefactFeedback: null,
  pendingDownloads: [],
  aiSettingsOptions: null,
  fetchAiSettingsOptionsPending: false,
  fetchAiSettingsOptionsFeedback: null,
  iManageBatchPayload: null,
  iManageStarted: null,
  iManageDocumentStatuses: {},
  iManageFailedDocuments: [],
};

export default initialState;
